<template>
	<Route
		:loading="loading"
		:routes="routes"
		:breadcrumbs="breadcrumbs"
		:route-title="report.displayName"
	>
		<template slot="title" v-if="!loading">
			{{ report.displayName }}
			<span
				v-if="report.isArchived || project.isArchived"
				class="route-archive-text"
			>
				(Archived)
			</span>
		</template>
		<template slot="breadcrumbs">
			<v-breadcrumbs
				:items="breadcrumbs"
				class="pt-1"
			>
				<template
					slot="item"
					slot-scope="{ item }"
				>
					<li>
						<v-menu
							v-if="item.dropdown"
							offset-y
							nudge-bottom="16"
						>
							<template
								slot="activator"
								slot-scope="{ on }"
							>
								<a
									class="v-breadcrumbs__item"
									href=""
									@click.prevent="on.click"
								>
									{{ item.text }} <v-icon class="ml-2">fa fa-caret-down</v-icon>
								</a>
							</template>
							<v-list>
								<v-list-tile
									v-for="(dropdownItem, index) in item.dropdownItems"
									:key="index"
									:to="dropdownItem.to"
								>
									<v-list-tile-title>{{ dropdownItem.text }}</v-list-tile-title>
								</v-list-tile>
							</v-list>
						</v-menu>
						<router-link
							v-else
							:to="item.to"
							class="v-breadcrumbs__item"
							:class="{ 'v-breadcrumbs__item--disabled': item.disabled }"
						>
							{{ item.text }}
						</router-link>
					</li>
				</template>
			</v-breadcrumbs>
		</template>
		<v-btn
			slot="additional"
			v-if="!$vuetify.breakpoint.xs"
			relative
			top
			right
			color="success"
			ripple
			@click="setOutput()"
		>
			Output Report
		</v-btn>
		<transition
			:name="transition"
			mode="out-in"
		>
			<keep-alive>
				<router-view :single-edit="singleEdit" />
			</keep-alive>
		</transition>
		<FullDialog
			:dialog="showOutput"
			dialog-title="Output Report"
			@close-dialog="closeOutput"
		>
			<template slot="dialog-content">
				<OutputView
					v-if="showOutput"
					:outputs="outputs"
					:initial-report="report"
					:reports="reports"
				/>
			</template>
		</FullDialog>
		<ClipboardAdd
			v-if="$store.get('isSkilled', 'clipboard_add')"
			@single-edit="setSingleEdit"
		/>
	</Route>
</template>

<script>
import Route from '@/views/layouts/Route';
import StoreMethods from '@/mixins/StoreMethods';
import FullDialog from '@/components/FullDialog';
import OutputView from '@/components/Report/OutputView';
import ClipboardAdd from '@/components/Report/ClipboardAdd';

export default {
	components: {
		Route,
		FullDialog,
		OutputView,
		ClipboardAdd,
	},
	mixins: [StoreMethods],
	data() {
		return {
			transition: '',
			loading: true,
			showOutput: false,
			singleEdit: null,
		};
	},
	created() {
		const reportId = parseInt(this.$route.params.reportId);

		/**
		 * @todo Will improve after issue #746
		 */
		this.$store.dispatch('reports/loadCurrentReportAndRelatedData', reportId).then((resp) => {
			dump('report/Index.vue', 'Current report and related data loaded', resp);

			const report = resp[0];

			this.$store.dispatch('projects/loadCurrentProjectAndRelatedData', report.projectId).then((resp) => {
				dump('report/Index.vue', 'Current project and related data loaded', resp);
			}).finally(() => {
				this.loading = false;
			});
		}).catch((e) => {
			this.$notify('error', 'Failed to retrieve Report.');
			this.$router.push({ name: 'Error' });
		});
	},
	computed: {
		project() {
			return this.$store.get('projects/currentProject');
		},
		report() {
			return this.$store.get('reports/currentReport');
		},
		reports() {
			return this.$store.get('reports/active').models;
		},
		outputs() {
			return this.project.sortedOutputs;
		},
		breadcrumbs() {
			if (this.loading) {
				return [];
			}

			const manageProjects = this.project.userCan('manage_projects');
			const manageUsers = this.project.userCan('manage_users');
			const manageCustomData = this.project.userCan('view_custom_data');
			const hasKeywordsSkill = this.$store.get('isSkilled', 'keywords');
			const isSingleProject = this.$store.get('projects/isSingleProject');

			const dropdownBaseUrl = `/projects/${this.project.id}`;

			return [
				{ text: 'Dashboard', to: '/', dropdown: false },
				...(isSingleProject ? [] : [{ text: 'Projects', to: '/projects', dropdown: false }]),
				{
					text: this.project.name,
					to: '',
					dropdown: true,
					dropdownItems: [
						{ text: 'Reports', to: `${dropdownBaseUrl}/reports` },
						{ text: 'Categories', to: `${dropdownBaseUrl}/categories` },
						...(hasKeywordsSkill ? [{ text: 'Keywords', to: `${dropdownBaseUrl}/keywords` }] : []),
						...(manageUsers ? [{ text: 'Users', to: `${dropdownBaseUrl}/users` }] : []),
						{ text: 'Outputs', to: `${dropdownBaseUrl}/outputs` },
						...(manageCustomData ? [{ text: 'Custom Data', to: `${dropdownBaseUrl}/customdata` }] : []),
						...manageProjects ? [{ text: 'Settings', to: `${dropdownBaseUrl}/settings` }] : [],
					]
				},
				{ text: this.report.displayName, to: '', disabled: true, dropdown: false },
			];
		},
		routes() {
			if (this.loading) {
				return [];
			}

			const hasReportPublicationsSkill = this.$store.get('isSkilled', 'report_publications');
			const manageReports = this.project.userCan('manage_reports');

			return [
				// { name: 'Overview', to: { name: 'ReportOverview' } },
				...(hasReportPublicationsSkill ? [{ name: 'Publications', to: { name: 'ReportPublications' } }] : []),
				{ name: 'Articles', to: { name: 'ArticlesList' } },
				{ name: 'Archive', to: { name: 'ArchiveList' } },
				{ name: 'Issues', to: { name: 'ReportIssues' }, highlight: this.hasIssues },
				...(manageReports ? [{ name: 'Settings', to: { name: 'ReportSettings' } }] : []),
			];
		},
		hasIssues() {
			const articles = this.$store.get('articles').filter(item => item.errors.length > 0);
			return articles.length > 0;
		},
	},
	methods: {
		setOutput() {
			this.showOutput = !this.showOutput;
			if (!this.showOutput) {
				document.documentElement.style.overflow = 'scroll';
			}
			else {
				document.documentElement.style.overflow = 'hidden';
			}
		},
		closeOutput() {
			this.showOutput = false;
		},
		setSingleEdit(article) {
			this.singleEdit = article;
			this.$router.push({ name: 'ArticlesList', params: { reportId: article.reportId } });
		},
	},
	/** @todo Duplicated in few places, find a way to DRY it out */
	beforeRouteUpdate(to, from, next) {
		const fromIndex = this.routes.findIndex(r => r.to.name === from.name);
		const toIndex = this.routes.findIndex(r => r.to.name === to.name);

		if (fromIndex === toIndex) {
			this.transition = '';
		}
		else if (fromIndex < toIndex) {
			this.transition = 'slide-left';
		}
		else {
			this.transition = 'slide-right';
		}

		next();
	},
};
</script>
